import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag, RefreshCw, ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { WirelessIssuePcbaNotesGlyph } from "@/components/manufacturing/WirelessIssuePcbaNotesGlyph";
import type { WirelessIssuePcbaNotesPreview } from "@/components/manufacturing/WirelessIssuePcbaNotesPanel";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import {
  HoverHintContent,
  HoverHintTrigger,
  HoverHintWithGlyph,
  PageTitleWithHelp,
} from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { BatchDetailLink, ShipmentDetailLink } from "@/components/hub/HubEntityDetailLink";
import { buildWirelessPortalDeviceDetailsUrl } from "@/lib/hubEntityDetailPaths";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { cn, EM_DASH, trimText } from "@/lib/utils";

import {
  fetchWirelessIssueWorkspace,
  setWirelessIssueFlag,
  WIRELESS_ISSUE_QUERY_KEY,
  type WirelessIssueBatchPeek,
  type WirelessIssueWorkspaceResponse,
  type WirelessIssueRecentCheckin,
  type WirelessIssueRecentCoreSaturationEvent,
  type WirelessIssueWorkspaceRow,
} from "./wirelessIssueApi";

type OriginBatchFilter = "all" | "2027" | "2062";
type FlaggedFilter = "all" | "flagged";
type WirelessIssueSortKey =
  | "pcba_serial"
  | "device_label_serial"
  | "imei"
  | "finished_sku"
  | "origin_batch_line_sku"
  | "origin_batch_id"
  | "origin_shipment_id"
  | "current_shipment_id"
  | "current_shipment_end_user"
  | "wireless_portal_org_name"
  | "wireless_portal_status"
  | "checkins_last_7_days"
  | "problem_tier"
  | "core_saturation_events_14d";
type WirelessIssueSortDir = "asc" | "desc";

function wirelessIssueEndUserFilterKey(endUser: string | null | undefined): string {
  const trimmed = endUser?.trim() ?? "";
  return trimmed === "" ? "(none)" : trimmed;
}

function toWirelessIssuePcbaNotesPreview(
  latestNote: WirelessIssueWorkspaceRow["latest_note"],
): WirelessIssuePcbaNotesPreview | null {
  if (latestNote == null) return null;
  return {
    text: latestNote.text,
    createdAt: latestNote.created_at,
    authorLabel: latestNote.author_label,
    deleted: latestNote.deleted,
  };
}

function compareNullableStrings(left: string | null | undefined, right: string | null | undefined): number {
  return (left ?? "").localeCompare(right ?? "", undefined, { sensitivity: "base", numeric: true });
}

function compareNullableNumbers(left: number | null | undefined, right: number | null | undefined): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return left - right;
}

function compareWirelessIssueProblemTierSortRank(row: WirelessIssueWorkspaceRow): number {
  if (row.problem_tier != null) return row.problem_tier;
  switch (row.problem_tier_status) {
    case "in_factory":
      return 4;
    case "normal":
      return 5;
    case "no_imei":
      return 6;
    default:
      return 99;
  }
}

function formatWirelessIssueProblemTierHoverHint(row: WirelessIssueWorkspaceRow): string {
  const metrics = [
    `Raw check-ins (7d): ${row.checkins_last_7_days.toLocaleString()}`,
    `Post-manufacturing check-ins (7d): ${row.checkins_last_7_days_post_mfg.toLocaleString()}`,
    `Max rolling 24h (post-mfg): ${row.max_checkins_24h_post_mfg.toLocaleString()}`,
    `>1600A core saturation events (14d): ${row.core_saturation_events_14d.toLocaleString()}`,
  ];
  if (row.problem_tier_reasons.length > 0) {
    metrics.push(`Reasons: ${row.problem_tier_reasons.join(", ")}`);
  }
  metrics.push(
    "Tier scoring excludes calibration and Final QC grace windows for check-in cadence. Any >1600A core saturation event on or after the current shipment date also triggers tier 3 (no recency window).",
  );
  return metrics.join("\n");
}

function WirelessIssueProblemTierCell({ row }: { row: WirelessIssueWorkspaceRow }): ReactNode {
  if (row.problem_tier_status === "no_imei") {
    return <span className="text-slate-500">{EM_DASH}</span>;
  }
  if (row.problem_tier_status === "in_factory") {
    return (
      <HoverHintContent hoverHint="Manufacturing milestones incomplete (calibration, Final QC pass, or shipment). Tier scoring not started yet.">
        <Badge tone="neutral" className="text-[10px]">
          Factory
        </Badge>
      </HoverHintContent>
    );
  }
  if (row.problem_tier_status === "normal") {
    return (
      <HoverHintContent hoverHint={formatWirelessIssueProblemTierHoverHint(row)} cursor="default">
        <span className="text-slate-500">{EM_DASH}</span>
      </HoverHintContent>
    );
  }
  const tone = row.problem_tier === 1 ? "danger" : row.problem_tier === 2 ? "warning" : "neutral";
  return (
    <HoverHintContent hoverHint={formatWirelessIssueProblemTierHoverHint(row)} cursor="default">
      <Badge tone={tone} className="text-[10px]">
        Tier {row.problem_tier}
      </Badge>
    </HoverHintContent>
  );
}

function compareWirelessIssueRows(
  left: WirelessIssueWorkspaceRow,
  right: WirelessIssueWorkspaceRow,
  sortKey: WirelessIssueSortKey,
  sortDir: WirelessIssueSortDir,
): number {
  let cmp = 0;
  switch (sortKey) {
    case "pcba_serial":
      cmp = compareNullableStrings(left.pcba_serial, right.pcba_serial);
      break;
    case "device_label_serial":
      cmp = compareNullableStrings(left.device_label_serial, right.device_label_serial);
      break;
    case "imei":
      cmp = compareNullableStrings(left.imei, right.imei);
      break;
    case "finished_sku":
      cmp = compareNullableStrings(left.finished_sku, right.finished_sku);
      break;
    case "origin_batch_line_sku":
      cmp = compareNullableStrings(left.origin_batch_line_sku, right.origin_batch_line_sku);
      break;
    case "origin_batch_id":
      cmp = left.origin_batch_id - right.origin_batch_id;
      break;
    case "origin_shipment_id":
      cmp = compareNullableNumbers(left.origin_shipment_id, right.origin_shipment_id);
      break;
    case "current_shipment_id":
      cmp = compareNullableNumbers(left.current_shipment_id, right.current_shipment_id);
      break;
    case "current_shipment_end_user":
      cmp = compareNullableStrings(left.current_shipment_end_user, right.current_shipment_end_user);
      break;
    case "wireless_portal_org_name":
      cmp = compareNullableStrings(left.wireless_portal_org_name, right.wireless_portal_org_name);
      break;
    case "wireless_portal_status":
      cmp = compareNullableStrings(left.wireless_portal_status, right.wireless_portal_status);
      break;
    case "checkins_last_7_days":
      cmp = left.checkins_last_7_days - right.checkins_last_7_days;
      break;
    case "problem_tier":
      cmp = compareWirelessIssueProblemTierSortRank(left) - compareWirelessIssueProblemTierSortRank(right);
      break;
    case "core_saturation_events_14d":
      cmp = left.core_saturation_events_14d - right.core_saturation_events_14d;
      break;
  }
  return sortDir === "asc" ? cmp : -cmp;
}

function WirelessIssueSortTh({
  column,
  children,
  sortKey,
  sortDir,
  onSort,
  className,
}: {
  column: WirelessIssueSortKey;
  children: ReactNode;
  sortKey: WirelessIssueSortKey;
  sortDir: WirelessIssueSortDir;
  onSort: (key: WirelessIssueSortKey) => void;
  className?: string;
}): JSX.Element {
  const active = sortKey === column;
  return (
    <th
      scope="col"
      className={cn("px-3 py-2 align-top", className)}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group inline-flex w-full min-w-0 items-center gap-0.5 font-inherit uppercase tracking-wide text-inherit hover:text-slate-800"
      >
        {children}
        <span
          className={cn("shrink-0 text-[10px] leading-none text-slate-500", !active && "opacity-0 group-hover:opacity-70")}
          aria-hidden
        >
          {active ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
        </span>
      </button>
    </th>
  );
}

function buildWorkspaceCacheRefreshHoverHint(workspace: WirelessIssueWorkspaceResponse | undefined): string {
  if (!workspace?.cached_at) {
    return "Reload wireless issue workspace from hub and wireless portal read replica.";
  }
  const status = workspace.revalidating ? "refreshing" : workspace.stale ? "stale" : "fresh";
  return `Workspace cache ${status} (${formatDateTime(workspace.cached_at)}). Shows the current snapshot while rebuilding in the background.`;
}

function OriginBatchCell({
  batchId,
  batchPeeks,
}: {
  batchId: number;
  batchPeeks: Record<number, WirelessIssueBatchPeek>;
}): ReactNode {
  const peek = batchPeeks[batchId];
  return (
    <BatchDetailLink
      batchId={batchId}
      className="font-mono text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:text-brand-900"
      peek={
        peek == null
          ? undefined
          : {
              mfr_ship_by_date: peek.mfr_ship_by_date,
              ship_to_label: peek.ship_to_label,
              shipment_on_hold: peek.shipment_on_hold,
              line_count: peek.line_count,
              serial_count: peek.serial_count,
            }
      }
    >
      {batchId}
    </BatchDetailLink>
  );
}

function ShipmentIdCell({
  shipmentId,
  shipmentDate,
  shipToLabel,
}: {
  shipmentId: number | null;
  shipmentDate: string | null;
  shipToLabel: string | null;
}): ReactNode {
  if (shipmentId == null) {
    return <span className="text-slate-500">{EM_DASH}</span>;
  }
  return (
    <ShipmentDetailLink
      shipmentId={shipmentId}
      className="font-mono text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:text-brand-900"
      peek={{ ship_date: shipmentDate, ship_to_label: shipToLabel }}
    >
      {shipmentId}
    </ShipmentDetailLink>
  );
}

function formatCheckinGps(lat: number | null, long: number | null): string | null {
  if (lat == null || long == null || !Number.isFinite(lat) || !Number.isFinite(long)) {
    return null;
  }
  return `${lat.toFixed(5)}, ${long.toFixed(5)}`;
}

function RecentCoreSaturationHoverHint({
  events,
}: {
  events: WirelessIssueRecentCoreSaturationEvent[];
}): ReactNode {
  if (events.length === 0) {
    return <span>No &gt;1600A core saturation events in the last 14 days.</span>;
  }
  return (
    <div className="max-w-[22rem] space-y-1 whitespace-normal text-left text-sm leading-snug">
      <div className="font-medium text-slate-900">Recent event details</div>
      <ul className="space-y-1">
        {events.map((event, index) => (
          <li key={`${event.event_timestamp}-${index}`}>
            <div>{formatDateTime(event.event_timestamp)}</div>
            <div className="text-slate-700">
              {event.event_type ?? "Event"}
              {event.fault_current != null && event.fault_current > 1600
                ? " · >1600A - Core Saturation"
                : event.fault_current != null
                  ? ` · ${event.fault_current}A`
                  : ""}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecentCheckinsHoverHint({ checkins }: { checkins: WirelessIssueRecentCheckin[] }): ReactNode {
  if (checkins.length === 0) {
    return <span>No check-ins in read replica.</span>;
  }
  return (
    <div className="space-y-1 text-left">
      <div className="font-medium text-slate-900">Most recent check-ins</div>
      <ul className="space-y-0.5">
        {checkins.map((checkin, index) => {
          const gps = formatCheckinGps(checkin.gps_lat, checkin.gps_long);
          return (
            <li key={`${checkin.checked_in_at}-${index}`}>
              {formatDateTime(checkin.checked_in_at)}
              {gps != null ? <span className="text-slate-600">{` · ${gps}`}</span> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ImeiCell({ imei }: { imei: string | null }): ReactNode {
  if (imei == null || imei.trim() === "") {
    return <span className="text-slate-500">{EM_DASH}</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 font-mono text-slate-800">
      <span>{imei}</span>
      <a
        href={buildWirelessPortalDeviceDetailsUrl(imei)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex text-brand-700 hover:text-brand-900"
        aria-label={`Open device ${imei} in wireless portal`}
      >
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
    </span>
  );
}

function formatDateTime(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


export function WirelessIssuePage() {
  const canWrite = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [originBatchFilter, setOriginBatchFilter] = useState<OriginBatchFilter>("all");
  const [selectedEndUsers, setSelectedEndUsers] = useState<Set<string>>(() => new Set());
  const [endUserFilterSearch, setEndUserFilterSearch] = useState("");
  const [flaggedFilter, setFlaggedFilter] = useState<FlaggedFilter>("all");
  const [sortState, setSortState] = useState<{ column: WirelessIssueSortKey; dir: WirelessIssueSortDir }>({
    column: "pcba_serial",
    dir: "asc",
  });

  const workspaceQuery = useQuery({
    queryKey: WIRELESS_ISSUE_QUERY_KEY,
    queryFn: () => fetchWirelessIssueWorkspace({ background: true }),
    staleTime: 5 * 60 * 1000,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.manufacturingWirelessIssueWorkspace],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: WIRELESS_ISSUE_QUERY_KEY });
      }
    },
  });

  const flagMutation = useMutation({
    mutationFn: setWirelessIssueFlag,
    onSuccess: async (result) => {
      queryClient.setQueryData<WirelessIssueWorkspaceResponse>(WIRELESS_ISSUE_QUERY_KEY, (current) => {
        if (current == null) return current;
        const pcbaSerial = result.flag.pcba_serial;
        return {
          ...current,
          rows: current.rows.map((row) =>
            row.pcba_serial === pcbaSerial
              ? {
                  ...row,
                  flagged: result.flag.flagged,
                  flag_updated_at: result.flag.updated_at,
                  flag_updated_by_email: result.flag.updated_by_email,
                }
              : row,
          ),
        };
      });
    },
  });

  const filteredRows = useMemo(() => {
    const rows = workspaceQuery.data?.rows ?? [];
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (originBatchFilter !== "all" && row.origin_batch_id !== Number(originBatchFilter)) {
        return false;
      }
      if (flaggedFilter === "flagged" && !row.flagged) {
        return false;
      }
      if (selectedEndUsers.size > 0) {
        if (!selectedEndUsers.has(wirelessIssueEndUserFilterKey(row.current_shipment_end_user))) {
          return false;
        }
      }
      if (needle === "") return true;
      const haystack = [
        row.pcba_serial,
        row.device_label_serial,
        row.imei,
        row.finished_sku,
        row.wireless_portal_org_name,
        row.wireless_portal_group_names,
        row.origin_ship_to_label,
        row.current_ship_to_label,
        row.current_shipment_end_user,
      ]
        .filter((value) => trimText(typeof value === "string" ? value : null) !== "")
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [workspaceQuery.data?.rows, search, originBatchFilter, selectedEndUsers, flaggedFilter]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((left, right) => compareWirelessIssueRows(left, right, sortState.column, sortState.dir));
    return copy;
  }, [filteredRows, sortState.column, sortState.dir]);

  const onSortHeader = (column: WirelessIssueSortKey): void => {
    setSortState((previous) =>
      previous.column === column
        ? { column, dir: previous.dir === "asc" ? "desc" : "asc" }
        : { column, dir: "asc" },
    );
  };

  const flaggedCount = useMemo(
    () => (workspaceQuery.data?.rows ?? []).filter((row) => row.flagged).length,
    [workspaceQuery.data?.rows],
  );

  const originBatchCounts = useMemo(() => {
    const rows = workspaceQuery.data?.rows ?? [];
    return {
      2027: rows.filter((row) => row.origin_batch_id === 2027).length,
      2062: rows.filter((row) => row.origin_batch_id === 2062).length,
    };
  }, [workspaceQuery.data?.rows]);

  const endUserFilterOptions = useMemo(() => {
    const rows = workspaceQuery.data?.rows ?? [];
    const counts = new Map<string, number>();
    let noneCount = 0;
    for (const row of rows) {
      const endUser = row.current_shipment_end_user?.trim() ?? "";
      if (endUser === "") {
        noneCount += 1;
        continue;
      }
      counts.set(endUser, (counts.get(endUser) ?? 0) + 1);
    }
    const options = [...counts.entries()]
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0], undefined, { sensitivity: "base" }))
      .map(([name, count]) => ({ value: name, label: `${name} (${count.toLocaleString()})` }));
    if (noneCount > 0) {
      options.push({ value: "(none)", label: `(none) (${noneCount.toLocaleString()})` });
    }
    return options;
  }, [workspaceQuery.data?.rows]);

  const toggleFlag = async (row: WirelessIssueWorkspaceRow) => {
    if (!canWrite) return;
    try {
      await flagMutation.mutateAsync({
        pcbaSerial: row.pcba_serial,
        flagged: !row.flagged,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        window.alert(error.message);
      } else {
        window.alert(error instanceof Error ? error.message : "Flag update failed.");
      }
    }
  };

  const replicaMeta = workspaceQuery.data?.replicaMeta;
  const batchPeeks = workspaceQuery.data?.batchPeeks ?? {};

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
          <PageTitleWithHelp
            className="shrink-0"
            hoverHint="Current hub serial rows for PCBA identities that first appeared on batches 2027 or 2062. Shipment columns show where units originally shipped and their present shipment assignment. Wireless portal org, status, and 7-day check-in counts come from the Supabase wireless_prod read replica (not live RDS)."
          >
            Wireless issue
          </PageTitleWithHelp>
          <span className="hidden h-4 w-px shrink-0 bg-slate-200 lg:block" aria-hidden />
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-700">
            <div className="flex shrink-0 items-center gap-2">
              <span className="font-medium text-slate-900">Wireless portal read replica</span>
              <HoverHintWithGlyph hoverHint="Counts and portal fields on this page query the wireless_prod schema on the Supabase read replica. Replication lag may be minutes behind the live Wireless portal database." />
            </div>
            <span className="hidden shrink-0 text-slate-300 lg:inline" aria-hidden>
              ·
            </span>
            <div className="shrink-0">
              Most recent <span className="font-mono text-xs">device_records</span> row:{" "}
              <span className="font-medium text-slate-900">
                {replicaMeta?.latestRecordAt ? formatDateTime(replicaMeta.latestRecordAt) : EM_DASH}
              </span>
            </div>
          </div>
        </div>
        <HoverHintTrigger
          align="start"
          trigger={
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="shrink-0"
              disabled={workspaceQuery.isFetching}
              onClick={() => {
                void queryClient.fetchQuery({
                  queryKey: WIRELESS_ISSUE_QUERY_KEY,
                  queryFn: () => fetchWirelessIssueWorkspace({ refresh: true }),
                });
              }}
            >
              <RefreshCw
                className={cn(
                  "mr-1.5 h-4 w-4",
                  (workspaceQuery.isFetching || workspaceQuery.data?.revalidating) && "animate-spin",
                )}
                aria-hidden
              />
              Refresh
            </Button>
          }
        >
          <span>{buildWorkspaceCacheRefreshHoverHint(workspaceQuery.data)}</span>
        </HoverHintTrigger>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="wireless-issue-search">Search devices</Label>
            <Input
              id="wireless-issue-search"
              type="search"
              className="h-10"
              placeholder="PCBA, device label, IMEI, org, end-user, ship-to…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wireless-issue-end-user">Current shipment end-user</Label>
            <SearchableMultiSelect
              id="wireless-issue-end-user"
              selectedValues={selectedEndUsers}
              onSelectedValuesChange={setSelectedEndUsers}
              options={endUserFilterOptions}
              searchValue={endUserFilterSearch}
              onSearchChange={setEndUserFilterSearch}
              placeholder="All end-users"
              searchPlaceholder="Search end-users…"
              allSelectedLabel="All end-users"
              emptyMessage="No end-users match."
              panelMinWidth={320}
              emptySelectionMeansAll
            />
          </div>
          <div className="space-y-1.5">
            <Label>Origin batch</Label>
            <div className="flex flex-wrap gap-2">
              {(["all", "2027", "2062"] as const).map((value) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={originBatchFilter === value ? "primary" : "secondary"}
                  onClick={() => setOriginBatchFilter(value)}
                >
                  {value === "all"
                    ? "All"
                    : `Batch ${value} (${originBatchCounts[value]})`}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Flagged</Label>
            <div className="flex flex-wrap gap-2">
              {(["all", "flagged"] as const).map((value) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={flaggedFilter === value ? "primary" : "secondary"}
                  onClick={() => setFlaggedFilter(value)}
                >
                  {value === "all" ? "All" : `Flagged only (${flaggedCount.toLocaleString()})`}
                </Button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>
            {sortedRows.length.toLocaleString()} device
            {sortedRows.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {workspaceQuery.isLoading ? (
            <p className="px-4 py-6 text-sm text-slate-600">Loading wireless issue workspace…</p>
          ) : workspaceQuery.isError ? (
            <p className="px-4 py-6 text-sm text-red-700">
              {workspaceQuery.error instanceof ApiError
                ? workspaceQuery.error.message
                : "Failed to load wireless issue workspace."}
            </p>
          ) : sortedRows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-600">No devices match these filters.</p>
          ) : (
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">Flag</th>
                  <WirelessIssueSortTh
                    column="pcba_serial"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    PCBA serial
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="device_label_serial"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Device label
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh column="imei" sortKey={sortState.column} sortDir={sortState.dir} onSort={onSortHeader}>
                    IMEI
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="origin_batch_line_sku"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Origin SKU
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="finished_sku"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Finished SKU
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="origin_batch_id"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Origin batch
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="origin_shipment_id"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Original shipment
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="current_shipment_id"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Current shipment
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="current_shipment_end_user"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    <HoverHintContent hoverHint="End-user on the sales order linked to the current shipment batch (client.data via end_user_fs_account_id).">
                      <span>Current shipment end-user</span>
                    </HoverHintContent>
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="wireless_portal_org_name"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Portal org
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="wireless_portal_status"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    Portal status
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="problem_tier"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    <HoverHintContent hoverHint="Problem tier from post-manufacturing check-in cadence. Tier 1 is saturated (900+ check-ins/7d or sustained high rate). Tier 2 is escalating watch. Tier 3 is early burst activity. Calibration and Final QC windows are excluded.">
                      <span>Problem tier</span>
                    </HoverHintContent>
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="checkins_last_7_days"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    <HoverHintContent hoverHint="Distinct device_records rows in the wireless_prod read replica with a timestamp in the last 7 days for this IMEI.">
                      <span>Check-ins (7d)</span>
                    </HoverHintContent>
                  </WirelessIssueSortTh>
                  <WirelessIssueSortTh
                    column="core_saturation_events_14d"
                    sortKey={sortState.column}
                    sortDir={sortState.dir}
                    onSort={onSortHeader}
                  >
                    <HoverHintContent hoverHint="Count of wireless_prod.event_records rows in the last 14 days where FaultCurrent is above 1600A (portal Event Details: >1600A - Core Saturation). Hover the count for the 10 most recent matching events.">
                      <span>&gt;1600A (14d)</span>
                    </HoverHintContent>
                  </WirelessIssueSortTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {sortedRows.map((row) => (
                  <tr
                    key={row.pcba_serial}
                    className={cn(row.flagged && "bg-amber-50/80")}
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={row.flagged ? "primary" : "secondary"}
                            disabled={!canWrite || flagMutation.isPending}
                            onClick={() => void toggleFlag(row)}
                            aria-label={row.flagged ? `Unflag ${row.pcba_serial}` : `Flag ${row.pcba_serial}`}
                          >
                            <Flag className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                          <WirelessIssuePcbaNotesGlyph
                            pcbaSerial={row.pcba_serial}
                            noteCount={row.note_count}
                            latestNote={toWirelessIssuePcbaNotesPreview(row.latest_note)}
                            hubWritesAllowed={canWrite}
                          />
                        </div>
                        {row.flagged && row.flag_updated_by_email ? (
                          <HoverHintContent
                            hoverHint={`Flagged by ${row.flag_updated_by_email}${row.flag_updated_at ? ` on ${formatDateTime(row.flag_updated_at)}` : ""}.`}
                          >
                            <Badge tone="warning" className="w-fit text-[10px]">
                              Flagged
                            </Badge>
                          </HoverHintContent>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top font-mono text-slate-900">{row.pcba_serial}</td>
                    <td className="px-3 py-2 align-top font-mono text-slate-800">
                      {row.device_label_serial ?? EM_DASH}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <ImeiCell imei={row.imei} />
                    </td>
                    <td className="px-3 py-2 align-top text-slate-800">{row.origin_batch_line_sku ?? EM_DASH}</td>
                    <td className="px-3 py-2 align-top text-slate-800">{row.finished_sku ?? EM_DASH}</td>
                    <td className="px-3 py-2 align-top">
                      <OriginBatchCell batchId={row.origin_batch_id} batchPeeks={batchPeeks} />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <ShipmentIdCell
                        shipmentId={row.origin_shipment_id}
                        shipmentDate={row.origin_shipment_date}
                        shipToLabel={row.origin_ship_to_label}
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <ShipmentIdCell
                        shipmentId={row.current_shipment_id}
                        shipmentDate={row.current_shipment_date}
                        shipToLabel={row.current_ship_to_label}
                      />
                    </td>
                    <td className="px-3 py-2 align-top text-slate-800">
                      {row.current_shipment_end_user ?? EM_DASH}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="space-y-0.5">
                        <div className="text-slate-900">{row.wireless_portal_org_name ?? EM_DASH}</div>
                        {row.wireless_portal_group_names ? (
                          <div className="text-slate-600">{row.wireless_portal_group_names}</div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-slate-800">
                      {row.wireless_portal_status ?? EM_DASH}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <WirelessIssueProblemTierCell row={row} />
                    </td>
                    <td className="px-3 py-2 align-top font-medium tabular-nums text-slate-900">
                      <HoverHintContent
                        hoverHint={<RecentCheckinsHoverHint checkins={row.recent_checkins} />}
                        cursor="default"
                      >
                        <span>{row.checkins_last_7_days.toLocaleString()}</span>
                      </HoverHintContent>
                    </td>
                    <td className="px-3 py-2 align-top font-medium tabular-nums text-slate-900">
                      <HoverHintContent
                        hoverHint={<RecentCoreSaturationHoverHint events={row.recent_core_saturation_events} />}
                        cursor="default"
                      >
                        <span>{row.core_saturation_events_14d.toLocaleString()}</span>
                      </HoverHintContent>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
